// App.js
import React, { Fragment, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@/App.scss";
import CamTest from "@/pages/CamTest";

import Wrapper from "@/layout/Wrapper";
import { ConfigProvider } from "antd";
import { Route, Routes } from "react-router-dom";
import Home from "@/pages/Home";
import FormLoader from "@/components/form/index";
import ScrollToTop from "@/components/routing/ScrollToTop";
import RequireAuth from "@/components/routing/RequireAuth";
import PrivacyPolicy from "@/pages/common/PrivacyPolicy";
import TermsAndCondtion from "@/pages/common/TermsAndCondtion";
import { loadStripe } from "@stripe/stripe-js";
import Checkout from "@/pages/checkout/Checkout";
import FaQ from "@/pages/faq/FaQ";
import { useSelector, useDispatch } from "react-redux";
import { getIsGuidePanel } from "@/reducers/guideSlice";
import { guideRoutes, studentRoutes } from "@/modules";
import DashboardDrawer from "@/components/DashboardDrawer/DashboardDrawer";
import Toaster, { showNotification } from "@/components/toaster/Toaster";
import CallLobby from "@/pages/session/CallLobby";
import CallUI from "@/pages/session/CallUI";
import LoaderWrapper from "@/pages/LoaderWrapper";
import GuideLobby from "@/pages/session/GuideLobby";
import CallEnd from "@/pages/session/CallEnd";
import MembershipPage from "@/pages/profile/MembershipPage";
import BundlePack from "@/components/homepage/BundlePack";
import PersonalizedCall from "@/components/homepage/PersonalizedCall";

import { setCurrentForm, setFormProps, setModalOpen } from "@/reducers/commonSlice";
import { extendSessionDuration } from "@/services/services/getAvailableGraceTime";
import OneSignal from "react-onesignal";
import DataRetentionPolicy from "@/pages/dataRetentionPolicy/dataRetentionPolicy";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import PasswordProtect from "@/pages/PasswordProtect";
import socket from "@/socket";

const queryClient = new QueryClient();
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_SECRET);

const GlobalSelectScrollFix = () => {
  useEffect(() => {
    // Original body overflow style to restore later
    let originalBodyStyle;
    let openDropdownCount = 0;

    // Function to check if any dropdown is open
    const checkDropdowns = () => {
      const openDropdowns = document.querySelectorAll(".ant-select-open");

      if (openDropdowns.length > 0) {
        // Save original style before modifying if this is the first open dropdown
        if (openDropdownCount === 0) {
          originalBodyStyle = document.body.style.overflow;
        }
        openDropdownCount = openDropdowns.length;
        // Prevent scrolling
        document.body.style.overflow = "hidden";
      } else {
        // Only restore scrolling if there are no open dropdowns
        if (openDropdownCount > 0) {
          document.body.style.overflow = originalBodyStyle || "";
          openDropdownCount = 0;
        }
      }
    };

    // Observer to detect class changes on ant-select elements
    const observer = new MutationObserver((mutations) => {
      // Check if any mutations involve adding/removing the ant-select-open class
      const relevantMutation = mutations.some((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class" &&
          mutation.target.classList.contains("ant-select")
        ) {
          return true;
        }
        return false;
      });

      if (relevantMutation) {
        checkDropdowns();
      }
    });

    // Find all select elements and observe them
    const observeSelects = () => {
      const selects = document.querySelectorAll(".ant-select");
      selects.forEach((select) => {
        observer.observe(select, {
          attributes: true,
          attributeFilter: ["class"],
        });
      });
    };

    // Initial observation
    observeSelects();

    // Also observe the body for new select elements being added
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          observeSelects();
        }
      });
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
      document.body.style.overflow = originalBodyStyle || "";
    };
  }, []);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const isGuidePanel = useSelector(getIsGuidePanel);

  const handleGraceTimeApproval = (data) => {
    // console.log("Received grace time request:", data);
    dispatch(setCurrentForm("approveRejectGraceTime"));
    dispatch(setModalOpen(true));
    dispatch(
      setFormProps({
        message: `The Student is requesting a grace period of ${data.time} minutes?`,
        onSubmit: async () => {
          try {
            // console.log("Approving grace time request:", data);
            const response = await extendSessionDuration(data.roomId, data.time);
            // console.log("Grace time approval response:", response);
            // Emit to both mentor and student
            socket.emit("updateDuration", {
              ...data,
              isApproved: true,
              message: response.msg,
              newDuration: response.maxDuration,
              isHost: true,
            });
            // Also emit a separate event for immediate time update
            socket.emit("forceTimeUpdate", {
              roomId: data.roomId,
              newDuration: response.maxDuration,
            });
            // Show notification only to mentor
            showNotification(
              "success",
              "Approved!",
              `You have approved the grace time request of ${data.time} minutes.`
            );
          } catch (error) {
            console.error("Error approving grace time:", error);
            showNotification(
              "error",
              "Error",
              "Failed to approve grace time request. Please try again."
            );
          }
          dispatch(setModalOpen(false));
        },
        onReject: () => {
          // console.log("Rejecting grace time request:", data);
          socket.emit("updateDuration", {
            ...data,
            isApproved: false,
            message: "Grace time request has been rejected.",
          });
          showNotification(
            "error",
            "Rejected!",
            `You have rejected the grace time request of ${data.time} minutes.`
          );
          dispatch(setModalOpen(false));
        },
      })
    );
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    // console.log("Joining socket with token:", token);
    socket.emit("joinSocket", { token });

    socket.on("graceTimeRequest", (data) => {
      // console.log("Received grace time request:", data);
      if (isGuidePanel) {
        handleGraceTimeApproval(data);
      }
    });

    socket.on("onUpdateDuration", (data) => {
      // console.log("Received duration update:", data);
      if (data.isApproved) {
        // Only show notification to student
        if (!isGuidePanel) {
          showNotification(
            "success",
            "Approved!",
            "The host has approved your grace time request."
          );
        }
        // Emit a custom event to trigger time update
        const event = new CustomEvent("graceTimeUpdated", {
          detail: {
            newDuration: data.newDuration,
            message: data.message,
            isHost: data.isHost,
          },
        });
        window.dispatchEvent(event);
      } else {
        // Only show notification to student
        if (!isGuidePanel) {
          showNotification("error", "Rejected!", "The host has rejected your grace time request.");
        }
      }
    });

    socket.on("forceTimeUpdate", (data) => {
      // console.log("Received force time update:", data);
      const event = new CustomEvent("graceTimeUpdated", {
        detail: {
          newDuration: data.newDuration,
          forceUpdate: true,
        },
      });
      window.dispatchEvent(event);
    });

    return () => {
      socket.off("graceTimeRequest");
      socket.off("onUpdateDuration");
      socket.off("forceTimeUpdate");
    };
  }, [dispatch, isGuidePanel]);

  useEffect(() => {
    const saveSubscriptionId = async () => {
      try {
        const subscriptionId = OneSignal?.User?.PushSubscription?.id;
        // console.log("subscriptionId", subscriptionId);

        if (subscriptionId) {
          localStorage.setItem("subscriptionId", subscriptionId);
        }
      } catch (error) {
        console.error("Error saving subscription ID:", error);
      }
    };

    saveSubscriptionId();
  }, []);

  const appContent = (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: "Poppins",
            colorPrimary: "#DB5800",
            algorithm: true,
            colorLinkHover: "#DB5800",
          },
          components: {
            Anchor: {
              colorLinkHover: "#DB5800",
            },
            Button: {
              colorLinkHover: "#DB5800",
              defaultBg: "transparent",
              defaultColor: "#DB5800",
              colorPrimary: "#DB5800",
              colorBorder: "#DB5800",
              controlHeightLG: 48,
              algorithm: true,
            },
            Tag: {
              defaultBg: "#FFF5F0",
              defaultColor: "#DB5800",
              algorithm: true,
            },
            Radio: {
              colorPrimary: "#1C1C2C",
            },
            Select: {
              colorBorder: "#1C1C2C",
            },
            Input: {
              colorBorder: "#1C1C2C",
            },
            DatePicker: {
              colorBorder: "#1C1C2C",
            },
          },
        }}
      >
        <GlobalSelectScrollFix />
        <Fragment>
          <LoaderWrapper />
          <Wrapper>
            <ScrollToTop />
            <Toaster />
            <Routes>
              <Route path="/camtest" element={<CamTest />} />
              <Route path="/" element={<Home />} />
              <Route path="/faq" element={<FaQ />} />
              <Route path="/data-retention-policy" element={<DataRetentionPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-condition" element={<TermsAndCondtion />} />
              <Route path="parent/lobby/:id" element={<CallLobby />} />
              <Route path="parent/meeting/:id/:roomCode" element={<CallUI />} />
              <Route path="guest/meeting/:id/:roomCode" element={<CallUI />} />
              <Route path="/membership" element={<MembershipPage />} />

              {/* Open Routes */}
              <Route path="/inforens-champions-bundle-pack" element={<BundlePack />} />
              <Route path="/personalised-call" element={<PersonalizedCall />} />
              {/* Protected Routes */}
              <Route
                path="guide/lobby/:id"
                element={
                  <RequireAuth>
                    <GuideLobby />
                  </RequireAuth>
                }
              />
              <Route
                path="guide/meeting/:id/:roomCode"
                element={
                  <RequireAuth>
                    <CallUI />
                  </RequireAuth>
                }
              />
              <Route path="/callend" element={<CallEnd />} />
              {!isGuidePanel
                ? studentRoutes?.map((route) => (
                    <Route
                      key={route.name}
                      path={route.path}
                      element={
                        route.isProtected ? (
                          <RequireAuth>{route.component}</RequireAuth>
                        ) : (
                          route.component
                        )
                      }
                    />
                  ))
                : guideRoutes?.map((route) => (
                    <Route
                      key={route.name}
                      path={route.path}
                      element={
                        route.isProtected ? (
                          <RequireAuth>{route.component}</RequireAuth>
                        ) : (
                          route.component
                        )
                      }
                    />
                  ))}
              {/* Only for student */}
              {!isGuidePanel ? (
                <Route
                  path="/checkout"
                  element={
                    <RequireAuth>
                      <Checkout stripePromise={stripePromise} />
                    </RequireAuth>
                  }
                />
              ) : null}
              <Route path="*" element={<Home />} />
            </Routes>
            <FormLoader />
            <DashboardDrawer />
          </Wrapper>
          <ReactQueryDevtools />
        </Fragment>
      </ConfigProvider>
    </QueryClientProvider>
  );

  if (import.meta.env.VITE_IS_PASSWORD_PROTECTED === "true") {
    return <PasswordProtect>{appContent}</PasswordProtect>;
  }

  return appContent;
};

export default App;
